/**
 * Utility functions for string manipulation
 */

/**
 * Generates a random alphanumeric string
 * @param length The length of the random string to generate
 * @returns A random string of the specified length
 */
export function generateRandomString(length: number = 15): string {
  const chars =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  return Array.from(
    { length },
    () => chars[Math.floor(Math.random() * chars.length)]
  ).join('');
}

/**
 * Removes specified query parameters from a URL, preserving the order of the
 * remaining params, the path, and any hash fragment.
 *
 * Rebuilds the query string from the surviving params rather than regex-deleting
 * matches: a regex that strips a leading `?param=…` leaves the next `&` dangling
 * (`/p?a=1&b=2` minus `a` → `/p&b=2`), which then mis-parses as a path. This is
 * reachable whenever a removed param can appear first (e.g. `attemptAutoLogin`).
 *
 * @param url The URL or router path to clean
 * @param params Param names to remove (defaults to ['code', 'state'])
 * @returns The cleaned URL without the specified parameters
 */
export function removeQueryParams(
  url: string,
  params: string[] = ['code', 'state']
): string {
  const hashIndex = url.indexOf('#');
  const hash = hashIndex >= 0 ? url.slice(hashIndex) : '';
  const beforeHash = hashIndex >= 0 ? url.slice(0, hashIndex) : url;

  const queryIndex = beforeHash.indexOf('?');
  if (queryIndex < 0) {
    return url;
  }

  const base = beforeHash.slice(0, queryIndex);
  const search = new URLSearchParams(beforeHash.slice(queryIndex + 1));
  for (const param of params) {
    search.delete(param);
  }

  const rebuilt = search.toString();
  return base + (rebuilt ? '?' + rebuilt : '') + hash;
}
